import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import axiosInstance from '../../../axiosInstance';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';

const emptyCustomer = {
  username: '',
  phone: '',
  email: '',
  pincode: '',
  state: '',
  address: '',
};

const toArray = (value) => (Array.isArray(value) ? value : []);

const getVariationValues = (variation) => {
  if (Array.isArray(variation?.value)) {
    return variation.value;
  }

  if (Array.isArray(variation?.[variation?.name])) {
    return variation[variation.name];
  }

  return [];
};

const getShippingCharge = (weight, shippingRatesStr) => {
  if (!shippingRatesStr) {
    return 0;
  }

  const shippingRates = String(shippingRatesStr)
    .split(',')
    .map((rateStr) => {
      const [rangeStr, chargeStr] = rateStr.split(':');
      const [minWeightStr, maxWeightStr] = rangeStr.split('-');
      return {
        minWeight: parseFloat(minWeightStr),
        maxWeight: parseFloat(maxWeightStr),
        charge: parseFloat(chargeStr),
      };
    })
    .filter((rate) => !Number.isNaN(rate.minWeight) && !Number.isNaN(rate.maxWeight) && !Number.isNaN(rate.charge));

  const rate = shippingRates.find((item) => weight >= item.minWeight && weight <= item.maxWeight);
  return rate?.charge || 0;
};

const CreateOrder = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [zones, setZones] = useState([]);
  const [customer, setCustomer] = useState(emptyCustomer);
  const [stateName, setStateName] = useState('');
  const [primaryName, setPrimaryName] = useState(false);
  const [userLookupStatus, setUserLookupStatus] = useState('');
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedVariations, setSelectedVariations] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [bookingDate, setBookingDate] = useState('');
  const [bookingTime, setBookingTime] = useState('');
  const [cartItems, setCartItems] = useState([]);
  const [shipping, setShipping] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [mode, setMode] = useState('COD');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedProduct = useMemo(
    () => products.find((product) => product._id === selectedProductId),
    [products, selectedProductId]
  );

  const selectedVariant = useMemo(() => {
    const variants = toArray(selectedProduct?.variant_products);
    const variationNames = toArray(selectedProduct?.variations).map((variation) => variation.name);

    if (!variants.length || !variationNames.length) {
      return null;
    }

    return variants.find((variant) => {
      if (variant.visible === false) {
        return false;
      }

      return variationNames.every((name) => !selectedVariations[name] || variant[name] === selectedVariations[name]);
    });
  }, [selectedProduct, selectedVariations]);

  const productPrice = Number(selectedVariant?.sale_price || selectedProduct?.salePrice || 0);
  const productRegularPrice = Number(selectedVariant?.regular_price || selectedProduct?.regularPrice || productPrice);
  const subtotal = cartItems.reduce((total, item) => total + Number(item.price || 0) * Number(item.quantity || 0), 0);
  const totalWeight = cartItems.reduce((total, item) => total + Number(item.weight || 0) * Number(item.quantity || 0), 0);
  const totalAmount = Math.max(0, subtotal + Number(shipping || 0) - Number(discount || 0));

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        const [productsRes, statesRes] = await Promise.all([
          axiosInstance.get('/all-home-products'),
          axiosInstance.get('/get-all-zones'),
        ]);

        setProducts(productsRes.data?.products || []);
        setZones(statesRes.data?.Zones || []);
      } catch (error) {
        toast.error('Error fetching create order data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const fetchTaxData = async (stateId, weight = totalWeight) => {
    if (!stateId) {
      setShipping(0);
      return;
    }

    try {
      const { data } = await axiosInstance.get(`/get-tax/${stateId}`);
      setShipping(getShippingCharge(weight, data?.tax?.rate));
    } catch (error) {
      setShipping(0);
    }
  };

  useEffect(() => {
    if (customer.state) {
      fetchTaxData(customer.state, totalWeight);
    }
  }, [customer.state, totalWeight]);

  useEffect(() => {
    const lookupUser = async () => {
      if (customer.phone.length !== 10) {
        setUserLookupStatus('');
        return;
      }

      setUserLookupStatus('Checking user...');

      try {
        const { data } = await axiosInstance.get(`/admin/user-by-phone/${customer.phone}`);

        if (data?.success && data?.existingUser) {
          const user = data.existingUser;
          setCustomer((prev) => ({
            ...prev,
            username: user.username || '',
            phone: user.phone || prev.phone,
            email: user.email || '',
            pincode: user.pincode || '',
            state: user.state || '',
            address: user.address || '',
          }));

          const selectedState = zones.find((item) => item._id === user.state);
          setStateName(selectedState?.name || user.statename || '');
          setPrimaryName(selectedState?.primary || false);
          setUserLookupStatus('Existing user found, details filled');
        } else {
          setCustomer((prev) => ({
            ...emptyCustomer,
            phone: prev.phone,
          }));
          setStateName('');
          setPrimaryName(false);
          setShipping(0);
          setUserLookupStatus('New user, enter details');
        }
      } catch (error) {
        setUserLookupStatus('Unable to check user');
      }
    };

    lookupUser();
  }, [customer.phone, zones]);

  const handleCustomerChange = (event) => {
    const { name, value } = event.target;
    const nextValue = name === 'phone' ? value.replace(/\D/g, '').slice(0, 10) : value;

    setCustomer((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
  };

  const handleStateChange = (event) => {
    const selectedOption = event.target.options[event.target.selectedIndex];
    const nextStateId = event.target.value;

    setCustomer((prev) => ({
      ...prev,
      state: nextStateId,
    }));
    setStateName(selectedOption.getAttribute('data-name') || '');
    setPrimaryName(selectedOption.getAttribute('data-primary') || false);
    fetchTaxData(nextStateId);
  };

  const handleProductChange = (event) => {
    setSelectedProductId(event.target.value);
    setSelectedVariations({});
    setQuantity(1);
  };

  const handleVariationChange = (name, value) => {
    setSelectedVariations((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const addItem = () => {
    if (!selectedProduct) {
      toast.error('Please choose product');
      return;
    }

    const missingVariation = toArray(selectedProduct.variations).find(
      (variation) => getVariationValues(variation).length && !selectedVariations[variation.name]
    );

    if (missingVariation) {
      toast.error(`Please choose ${missingVariation.name}`);
      return;
    }

    if (!bookingDate || !bookingTime) {
      toast.error('Please choose date and time');
      return;
    }

    const variationSuffix = Object.values(selectedVariations).filter(Boolean).join(' ');
    const title = variationSuffix ? `${selectedProduct.title} ${variationSuffix}` : selectedProduct.title;
    const itemId = `${selectedProduct._id}-${Object.values(selectedVariations).filter(Boolean).join('-') || 'default'}`;
    const productVendor = selectedProduct?.userId || {};
    const productCategories = Array.isArray(selectedProduct?.Category)
      ? selectedProduct.Category.map((category) => category?._id || category).filter(Boolean)
      : [];

    setCartItems((prev) => {
      const existingIndex = prev.findIndex((item) => item.id === itemId);
      const nextItem = {
        id: itemId,
        pid: selectedProduct._id,
        title,
        image: selectedProduct.pImage,
        regularPrice: productRegularPrice,
        price: productPrice,
        quantity: Number(quantity || 1),
        TotalQuantity: Number(quantity || 1),
        SelectedSizes: selectedVariations,
        weight: Number(selectedProduct.weight || 0),
        stock: Number(selectedProduct.stock || 999999),
        slug: selectedProduct.slug,
        bookingDate,
        bookingTime,
        vendorId: productVendor?._id || selectedProduct?.vendorId || '',
        vendorName: productVendor?.username || selectedProduct?.vendorName || '',
        vendorPhone: productVendor?.phone || selectedProduct?.vendorPhone || '',
        category: productCategories,
        longitude: productVendor?.longitude || '',
        latitude: productVendor?.latitude || '',
      };

      if (existingIndex === -1) {
        return [...prev, nextItem];
      }

      return prev.map((item, index) =>
        index === existingIndex
          ? { ...item, quantity: Number(item.quantity || 0) + Number(quantity || 1) }
          : item
      );
    });

    toast.success('Item added');
  };

  const removeItem = (itemId) => {
    setCartItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const submitOrder = async (event) => {
    event.preventDefault();

    if (!customer.username || !customer.phone || !customer.state || !customer.address || !cartItems.length) {
      toast.error('Please fill customer details, state, and add product');
      return;
    }

    setIsSubmitting(true);
    const firstVendorItem = cartItems.find((item) => item.vendorId) || {};
    const orderCategories = [
      ...new Set(
        cartItems.flatMap((item) => {
          if (!item.category) return [];
          return Array.isArray(item.category) ? item.category : [item.category];
        })
      ),
    ].filter(Boolean);

    const payload = {
      ...customer,
      statename: stateName,
      bookingDate,
      bookingTime,
      items: cartItems,
      status: 1,
      mode,
      details: [
        {
          username: customer.username,
          phone: customer.phone,
          pincode: customer.pincode,
          state: stateName,
          address: customer.address,
          email: customer.email,
        },
      ],
      discount: Number(discount || 0),
      shipping: Number(shipping || 0),
      totalAmount,
      vendorId: firstVendorItem.vendorId || '',
      asignId: firstVendorItem.vendorId || '',
      category: orderCategories,
      longitude: firstVendorItem.longitude || '',
      latitude: firstVendorItem.latitude || '',
      primary: primaryName,
      payment: mode === 'COD' ? 0 : 1,
      verified: 1,
    };

    try {
      const { data } = await axiosInstance.post('/admin/create-order-without-iframe', payload);
      toast.success(data?.message || 'Order created successfully');
      navigate('/all-order');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error creating order');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />
        <div className="container-fluid py-4">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h1 className="h3 mb-0">Create Order</h1>
            <button className="btn btn-outline-primary" type="button" onClick={() => navigate('/create-order-iframe')}>
              Open iframe order
            </button>
          </div>

          <form onSubmit={submitOrder}>
            <div className="row g-4">
              <div className="col-lg-7">
                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-header">
                    <h2 className="h5 mb-0">Customer</h2>
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label">Name</label>
                        <input className="form-control" name="username" value={customer.username} onChange={handleCustomerChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Phone</label>
                        <input className="form-control" name="phone" value={customer.phone} maxLength={10} onChange={handleCustomerChange} />
                        {userLookupStatus && <small className="text-muted">{userLookupStatus}</small>}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Email</label>
                        <input className="form-control" name="email" value={customer.email} onChange={handleCustomerChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Pincode</label>
                        <input className="form-control" name="pincode" value={customer.pincode} onChange={handleCustomerChange} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">State</label>
                        <select className="form-select" name="state" value={customer.state} onChange={handleStateChange}>
                          <option value="">{isLoading ? 'Loading...' : 'Select State'}</option>
                          {zones.map((item) => (
                            <option key={item._id} value={item._id} data-name={item.name} data-primary={item.primary}>
                              {item.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12">
                        <label className="form-label">Address</label>
                        <textarea className="form-control" name="address" rows="3" value={customer.address} onChange={handleCustomerChange} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card border-0 shadow-sm">
                  <div className="card-header">
                    <h2 className="h5 mb-0">Product</h2>
                  </div>
                  <div className="card-body">
                    <div className="row g-3 align-items-end">
                      <div className="col-md-7">
                        <label className="form-label">Select product</label>
                        <select className="form-select" value={selectedProductId} onChange={handleProductChange} disabled={isLoading}>
                          <option value="">Choose product</option>
                          {products.map((product) => (
                            <option key={product._id} value={product._id}>
                              {product.title}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-2">
                        <label className="form-label">Qty</label>
                        <input className="form-control" type="number" min="1" value={quantity} onChange={(event) => setQuantity(event.target.value)} />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Choose Date</label>
                        <input className="form-control" type="date" value={bookingDate} onChange={(event) => setBookingDate(event.target.value)} />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Choose Time</label>
                        <input className="form-control" type="time" value={bookingTime} onChange={(event) => setBookingTime(event.target.value)} />
                      </div>
                      <div className="col-md-3">
                        <button className="btn btn-primary w-100" type="button" onClick={addItem}>
                          Add Item
                        </button>
                      </div>
                    </div>

                    {selectedProduct && (
                      <div className="row g-3 mt-1">
                        {toArray(selectedProduct.variations).map((variation) => {
                          const values = getVariationValues(variation);
                          if (!values.length) {
                            return null;
                          }

                          return (
                            <div className="col-md-4" key={variation.name}>
                              <label className="form-label">{variation.name}</label>
                              <select
                                className="form-select"
                                value={selectedVariations[variation.name] || ''}
                                onChange={(event) => handleVariationChange(variation.name, event.target.value)}
                              >
                                <option value="">Choose</option>
                                {values.map((value) => (
                                  <option key={value} value={value}>
                                    {value}
                                  </option>
                                ))}
                              </select>
                            </div>
                          );
                        })}
                        <div className="col-md-4">
                          <label className="form-label">Price</label>
                          <input className="form-control" value={productPrice} readOnly />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-5">
                <div className="card border-0 shadow-sm">
                  <div className="card-header">
                    <h2 className="h5 mb-0">Order Summary</h2>
                  </div>
                  <div className="card-body">
                    {cartItems.length ? (
                      <div className="table-responsive">
                        <table className="table align-items-center">
                          <thead>
                            <tr>
                              <th>Item</th>
                              <th>Qty</th>
                              <th>Total</th>
                              <th />
                            </tr>
                          </thead>
                          <tbody>
                            {cartItems.map((item) => (
                              <tr key={item.id}>
                                <td>{item.title}</td>
                                <td>{item.quantity}</td>
                                <td>Rs. {Number(item.price || 0) * Number(item.quantity || 0)}</td>
                                <td>
                                  <button className="btn btn-sm btn-outline-danger" type="button" onClick={() => removeItem(item.id)}>
                                    Remove
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-muted">No items added.</p>
                    )}

                    <div className="row g-3 mt-2">
                      <div className="col-md-6">
                        <label className="form-label">Shipping</label>
                        <input className="form-control" type="number" min="0" value={shipping} onChange={(event) => setShipping(event.target.value)} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Discount</label>
                        <input className="form-control" type="number" min="0" value={discount} onChange={(event) => setDiscount(event.target.value)} />
                      </div>
                      <div className="col-12">
                        <label className="form-label">Payment Mode</label>
                        <select className="form-select" value={mode} onChange={(event) => setMode(event.target.value)}>
                          <option value="COD">COD</option>
                          <option value="Online">Online</option>
                        </select>
                      </div>
                    </div>

                    <hr />

                    <div className="d-flex justify-content-between">
                      <span>Subtotal</span>
                      <strong>Rs. {subtotal}</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>Total</span>
                      <strong>Rs. {totalAmount}</strong>
                    </div>

                    <button className="btn btn-success w-100 mt-4" type="submit" disabled={isSubmitting}>
                      {isSubmitting ? 'Creating...' : 'Create Order'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </main>
    </>
  );
};

export default CreateOrder;
